import { createContext, useState, type ReactNode } from 'react';
import { IconRail, type RailItem } from '@/components/shell/IconRail';
import { TopBar } from '@/components/shell/TopBar';

/**
 * The shell's top-bar actions target. The active module reads this and portals
 * its primary actions onto the search line; `null` (no provider, e.g. an
 * isolated unit test) means the module renders those actions inline instead.
 */
export const ShellActionsContext = createContext<HTMLElement | null>(null);

export interface AppShellProps {
  navItems: RailItem[];
  children: ReactNode;
  /** Opens central search (Cmd+K) — module 0.8. */
  onOpenSearch?: () => void;
  /** Fired with a nav item id when the rail selection changes (App routes it). */
  onSelectNav?: (id: string) => void;
  /** Primary actions for the top bar. */
  actions?: ReactNode;
  /**
   * Detail/workbench content rendered as an overlay ON TOP of the content
   * (plan §3.2 — details never shrink the calendar). Absent = nothing shown.
   */
  overlay?: ReactNode;
}

/**
 * The dark-first application frame (plan §3.2, docs/UI_STANDARDS.md §3): a thin
 * top bar + a collapsible icon rail wrap a max-space content area. Details open
 * as an overlay above the content, never as a space-taking side panel.
 */
export function AppShell({ navItems, children, onOpenSearch, onSelectNav, actions, overlay }: AppShellProps): ReactNode {
  const [actionsEl, setActionsEl] = useState<HTMLElement | null>(null);
  return (
    <div className="flex h-screen flex-col bg-background text-foreground">
      <TopBar onOpenSearch={onOpenSearch} actions={actions} actionsRef={setActionsEl} />
      <div className="flex min-h-0 flex-1">
        <IconRail items={navItems} onSelect={onSelectNav} />
        <main className="relative min-w-0 flex-1 overflow-auto">
          <ShellActionsContext.Provider value={actionsEl}>{children}</ShellActionsContext.Provider>
          {overlay ? (
            <div role="dialog" aria-modal="true" className="absolute inset-0 z-10 bg-background/80 p-6">
              {overlay}
            </div>
          ) : null}
        </main>
      </div>
    </div>
  );
}
